import React, { useState, useRef, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PasswordIcon from '@mui/icons-material/Password';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Button } from '@material-ui/core';
import { Box, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CSVLink } from 'react-csv';

const CustomTable = ({
  tableName,
  columns,
  data,
  enableColumnResizing = false,
  rowsPerPageOptions,
  onDocuments,
  showDocuments = false,
  onCharges,
  showCharges = false,
  showEdit = true,
  showDelete = true,
  showResetPassword = false,
  onAdd,
  additionalBtn,
  onEdit,
  onDelete,
  onHandlePassword,
  showCreateButton = true,
  showAdditionalButton = false,
  additionalBtnDisabled,
  buttonText = 'CREATE',
  initialVisibility = null,
  visibility = null,
  showView,
  onView,
  hasExport = false,
  filename = 'export_csv',
  manualPagination = false,
  manualSorting = false,
  manualFiltering = false,
  onGlobalFilterChange,
  onPaginationChange,
  onSortingChange,
  state,
  rowCount,
  enableColumnFilters = true,
  rowActionRendererer = null,
}) => {
  const isFirstRender = useRef(true);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: parseInt(window.localStorage.getItem(tableName)) || 10,
  });

  // Column visibility state with persistence
  const visibilityKey = `${tableName}-visibility`;
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const stored = window.localStorage.getItem(visibilityKey);
    return stored ? JSON.parse(stored) : initialVisibility || {};
  });

  // Load saved page size and avoid overriding first render
  useEffect(() => {
    const pageSize = window.localStorage.getItem(tableName);
    if (pageSize) {
      setPagination({ pageIndex: 0, pageSize: parseInt(pageSize) });
    }
    isFirstRender.current = false;
  }, []);

  // Save page size
  useEffect(() => {
    if (!isFirstRender.current) {
      window.localStorage.setItem(tableName, pagination.pageSize);
    }
  }, [pagination.pageSize, tableName]);

  // Save visibility state
  useEffect(() => {
    window.localStorage.setItem(visibilityKey, JSON.stringify(columnVisibility));
  }, [columnVisibility, visibilityKey]);

  // Action handlers...
  const handleAdd = () => onAdd && onAdd();
  const handleEdit = row => onEdit && onEdit({ data: row.original });
  const handleView = row => onView && onView({ data: row.original });
  const handleDelete = row => onDelete && onDelete({ data: row.original });
  const handleAdditionalBtn = () => additionalBtn && additionalBtn();
  const handleCharges = row => onCharges && onCharges({ data: row.original });
  const handleDocuments = row => onDocuments && onDocuments({ data: row.original });
  const handleResetPassword = row => onHandlePassword && onHandlePassword({ data: row.original });

  const standardActionRenderer = row => (
    <>
      {showEdit && (
        <Tooltip title="Edit" arrow>
          <EditIcon fontSize="small" onClick={() => handleEdit(row)} style={{ cursor: 'pointer' }} />
        </Tooltip>
      )}
      {showDelete && (
        <Tooltip title="Delete" arrow>
          <DeleteIcon fontSize="small" onClick={() => handleDelete(row)} style={{ marginLeft: 10, cursor: 'pointer' }} />
        </Tooltip>
      )}
      {showCharges && (
        <Tooltip title="Change price" arrow>
          <PriceChangeIcon fontSize="small" onClick={() => handleCharges(row)} style={{ marginLeft: 10, cursor: 'pointer' }} />
        </Tooltip>
      )}
      {showResetPassword && (
        <Tooltip title="Change password" arrow>
          <PasswordIcon fontSize="small" onClick={() => handleResetPassword(row)} style={{ marginLeft: 10, cursor: 'pointer' }} />
        </Tooltip>
      )}
      {showView && (
        <Tooltip title="View invoice" arrow>
          <VisibilityIcon fontSize="small" onClick={() => handleView(row)} style={{ marginLeft: 10, cursor: 'pointer' }} />
        </Tooltip>
      )}
      {showDocuments && (
        <Tooltip title="Manage documents" arrow>
          <NoteAddIcon fontSize="small" onClick={() => handleDocuments(row)} style={{ marginLeft: 10, cursor: 'pointer' }} />
        </Tooltip>
      )}
    </>
  );

  const materialOptions = {
    columns,
    data,
    enableColumnResizing,
    state: {
      pagination,
      columnVisibility,
      ...state,
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        muiTableHeadCellProps: { align: 'center' },
        size: 120,
      },
    },
    enableRowActions: showEdit || showDelete || showCharges || showResetPassword || showView || showDocuments,
    positionActionsColumn: 'last',
    rowCount,
    manualFiltering,
    manualPagination,
    manualSorting,
    enableColumnFilters,
    onPaginationChange: onPaginationChange || setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}>
        {hasExport && <CSVLink filename={filename} data={data}>Export data</CSVLink>}
      </Box>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {rowActionRendererer ? rowActionRendererer(row) : standardActionRenderer(row)}
      </Box>
    ),
  };

  if (manualSorting) materialOptions.onSortingChange = onSortingChange;
  if (manualFiltering) materialOptions.onGlobalFilterChange = onGlobalFilterChange;

  const table = useMaterialReactTable(materialOptions);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={6} mb={2}
      >
        <Typography variant="h5">{tableName}</Typography>
        <Box>
          {showAdditionalButton && (
            <Button variant="contained" size="large" sx={{ fontWeight: 'bold', mr: 2 }} onClick={handleAdditionalBtn} disabled={additionalBtnDisabled}>
              {buttonText}
            </Button>
          )}
          {showCreateButton && (
            <Button variant="contained" size="large" sx={{ fontWeight: 'bold' }} startIcon={<AddIcon />} onClick={handleAdd}>
              ADD
            </Button>
          )}
        </Box>
      </Box>
      <MaterialReactTable table={table} />
    </>
  );
};

export default CustomTable;
